/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The ads bidBrain produced — the side of the mapping you map **to**.
 *
 * The inspector showed the request sent, the raw response, and the parsed
 * `response.*` roots. All three are the *from* side: what is available to map.
 * Nothing showed the result, so the response-mapping surface promised "what
 * values are extracted back into the session" and then offered no evidence that
 * anything had been. Parsing something is not the same as mapping it.
 *
 * These ads are the real output of the whole pipeline — the driver's structural
 * extraction, the default mapping document, and the operator's authored mapping,
 * all resolved — and they are the same objects the optimizer will consume. Not a
 * simulation of one row, which is what the per-row "resolves to" preview (R4a,
 * #898) will add at authoring time. The two answer different questions: that one
 * is "what will this row do", this one is "what did the whole thing do".
 *
 * Custom mapped targets show up here and **nowhere else** — not in the catalog,
 * not in autocomplete, not in the parsed roots — because `applyResponseMapping`
 * writes arbitrary targets straight onto the ad.
 */

import React from 'react';
import { Box, Flex, Table, Text } from '@chakra-ui/react';
import { OPTIMIZER_FIELDS, type ParsedAd } from '../../utils/test-diagnosis';
import { PayloadViewer } from './PayloadViewer';

/** Fields the table already accounts for; everything else is "extra". */
const TABLED_FIELDS = new Set<string>([
  ...OPTIMIZER_FIELDS.map((f) => f.field as string),
  // Structural bookkeeping the operator did not map and does not need a column for.
  'durationSeconds',
  // The driver's position within its own source's response. Renamed from `sequence`
  // (#967), which claimed to be the pod position and was not.
  'sourceIndex',
  'podId',
  'demandSource',
  'rawMarkup',
  'injectedTracking',
]);

/**
 * Targets present on a produced ad that no column covers — a custom mapping's
 * only visible proof. Collected across all ads so a target mapped on one ad and
 * not another still surfaces.
 */
function extraTargets(ads: readonly Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  for (const ad of ads) {
    for (const [key, value] of Object.entries(ad)) {
      if (TABLED_FIELDS.has(key)) continue;
      if (value === undefined || value === null || value === '') continue;
      seen.add(key);
    }
  }
  return [...seen].sort();
}

interface ProducedAdsViewProps {
  ads: readonly ParsedAd[];
}

export const ProducedAdsView: React.FC<ProducedAdsViewProps> = ({ ads }) => {
  const [showRaw, setShowRaw] = React.useState(false);

  if (ads.length === 0) {
    return (
      <Text fontSize="xs" color="fg.muted">
        No ads were produced, so there is nothing to show here. The response above is what came back.
      </Text>
    );
  }

  const extras = extraTargets(ads as readonly Record<string, unknown>[]);

  return (
    <Box>
      <Flex align="baseline" justify="space-between" gap={2} mb={2} wrap="wrap">
        <Text fontSize="xs" color="fg.muted">
          {ads.length} ad{ads.length === 1 ? '' : 's'} came out of the mapping — this is what the optimizer receives.
        </Text>
        <Text
          as="button"
          fontSize="xs"
          color="accent.solid"
          onClick={() => setShowRaw((v) => !v)}
          _hover={{ textDecoration: 'underline' }}
        >
          {showRaw ? 'Show table' : 'Show full objects'}
        </Text>
      </Flex>

      {showRaw ? (
        <PayloadViewer
          value={JSON.stringify(ads, null, 2)}
          language="json"
          label="Produced ads — every field, including custom mapped targets"
          maxHeight="320px"
        />
      ) : (
        <Box borderWidth="1px" borderColor="border.default" borderRadius="6px" overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader fontSize="xs" color="fg.muted">
                  #
                </Table.ColumnHeader>
                {OPTIMIZER_FIELDS.map((spec) => (
                  <Table.ColumnHeader key={spec.field as string} fontSize="xs" color="fg.muted">
                    {spec.label}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {ads.map((ad, i) => (
                <Table.Row key={ad.adId ?? i}>
                  <Table.Cell fontSize="xs" color="fg.muted">
                    {i + 1}
                  </Table.Cell>
                  {OPTIMIZER_FIELDS.map((spec) => {
                    const populated = spec.isPopulated(ad);
                    return (
                      <Table.Cell
                        key={spec.field as string}
                        fontSize="xs"
                        fontFamily="mono"
                        // A required field that is absent is the reason an ad
                        // cannot be used; an advisory one only matters if a rule
                        // references it, so it stays quiet.
                        color={populated ? 'fg.default' : spec.severity === 'required' ? 'error.ink' : 'fg.muted'}
                        maxW="240px"
                        truncate
                        title={populated ? spec.format(ad) : `not populated — ${spec.consequence}`}
                      >
                        {populated ? spec.format(ad) : '—'}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      {extras.length > 0 && !showRaw && (
        <Text fontSize="xs" color="fg.muted" mt={2}>
          Also mapped:{' '}
          <Text as="span" fontFamily="mono" color="fg.default">
            {extras.join(', ')}
          </Text>{' '}
          — open the full objects to see the values.
        </Text>
      )}
    </Box>
  );
};
